/**
 * What the server wrote into the page for this request (SpaShellMiddleware), read once in main.ts.
 * The theme's colours, font, logo url and the third-party script urls are not here: the server puts
 * those into the page itself rather than handing them to the client to place.
 */
export class UIAppConfig {
  agGridKey: string;
  logToSiteUsageTracker: boolean;
  leftNavLinks: LeftNavLinks;
  mobileAppLinks: MobileAppLinks;
  currentTheme: ICurrentTheme;
  features: { [key: string]: boolean };
  readonly defaultPageSize: number = 50;
}

export class LeftNavLinks {
  legacyAppLoginUrl: string;
  downloadResourcesUrl: string;
  quickReferenceGuideUrl: string;
  selfInstallGuideUrl: string;
  customerServiceUrl: string;
}

/** The app's store listings, for the login page's badges. Empty where the environment sets none. */
export class MobileAppLinks {
  appleStore: string;
  googlePlay: string;
}

export class CurrentTheme implements ICurrentTheme {
  get themeId(): number {
    return this.itheme.themeId;
  }
  get themeName(): string {
    return this.itheme.themeName;
  }
  get productName(): string {
    return this.itheme.productName;
  }
  get themePathSegment(): string {
    return this.itheme.themePathSegment;
  }
  get customerLogoHyperlink(): string {
    return this.itheme.customerLogoHyperlink;
  }

  /**
   * Built from the theme id alone. The server serves an approved theme's logo from this origin,
   * so no url from the theme record reaches an image.
   */
  get LogoUrlPath(): string {
    return this.themeId
      ? `api/v1/Theming/${this.themeId}/logo`
      : 'assets/images/Dashboard_logo@3x.png';
  }

  get heartbeatLogoUrlPath(): string {
    return this.themeId ? this.LogoUrlPath : 'assets/images/logo.png';
  }

  constructor(private itheme: ICurrentTheme) {}
}

export interface ICurrentTheme {
  themeId: number;
  themeName: string;
  /**
   * What this theme calls the product, shown in the browser tab title. Absent on an unthemed visit,
   * where the app falls back to its default brand - see AppTitleStrategy.
   */
  productName?: string;
  /**
   * Leading path segment every route is pinned under, e.g. 'acme' in /acme/dashboard. Resolved by
   * the server from the request it served, and null unless that URL was genuinely under a theme
   * slug - see ThemeRouter.
   */
  themePathSegment?: string;
  customerLogoHyperlink: string;
}
